import React, { useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { setNavigate } from '@utils/navigation';

import { useConfigurationStore } from '@store/use-configuration-store';
import { useUserStore } from '@store/use-user-store';

import {
  getModuleStartRoute,
  getWorkflowFromModule,
  ROUTES
} from './constants/routes';
import LogoutPage from './pages/logout';
import {
  authenticatedRoutes,
  RouteConfig,
  unauthenticatedRoutes
} from './route-config';

function NavigationSetter() {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  return null;
}

function ScrollToTop() {
  const { pathname, key } = useLocation();

  useEffect(() => {
    // 'default' key means the user arrived via back/forward — let the browser restore scroll
    if (key !== 'default') {
      window.scrollTo(0, 0);
      document.getElementById('leftContainer')?.scrollTo(0, 0);
    }
  }, [pathname, key]);

  return null;
}

// Chrome's back button can skip our in-app history and land on a page from an
// earlier document load (e.g. /visitor-registration/login). That can't be
// blocked, so pages of another workflow redirect to the active one instead.
function WorkflowGuard({ children }: { children: React.ReactElement }) {
  const { pathname } = useLocation();
  const { module } = useConfigurationStore();
  const { username } = useUserStore();
  const workflowPrefixes = [
    'visitor-registration',
    'elearning-wp-remote',
    'elearning-external',
    'elearning-wp-onsite'
  ];
  const pathWorkflow = pathname.split('/')[1];

  useEffect(() => {
    // A page restored from the back/forward cache keeps its old in-memory
    // state, so reload it to pick up the active workflow from storage
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) window.location.reload();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  if (
    module &&
    workflowPrefixes.includes(pathWorkflow) &&
    pathWorkflow !== getWorkflowFromModule(module)
  ) {
    return <Navigate to={getModuleStartRoute(module, !!username)} replace />;
  }

  return children;
}

function renderRoutes(routes: RouteConfig[]) {
  return routes.map(({ path, component: Component, wrapper: Wrapper }) => (
    <Route
      key={path}
      path={path}
      element={
        Wrapper ? (
          <Wrapper>
            <Component />
          </Wrapper>
        ) : (
          <Component />
        )
      }
    />
  ));
}

function Router() {
  const { username } = useUserStore();
  const isLogin = !!username;
  return (
    <BrowserRouter basename={'/'}>
      <NavigationSetter />
      <ScrollToTop />
      <WorkflowGuard>
        {isLogin ? (
          <Routes>
            {renderRoutes(authenticatedRoutes)}
            <Route path={ROUTES.LOGOUT} element={<LogoutPage />} />
            <Route path="*" element={<Navigate to={ROUTES.VR_WELCOME} />} />
          </Routes>
        ) : (
          <Routes>
            {renderRoutes(unauthenticatedRoutes)}
            <Route path={ROUTES.LOGOUT} element={<LogoutPage />} />
            <Route path="*" element={<Navigate to={ROUTES.VR_LOGIN} />} />
          </Routes>
        )}
      </WorkflowGuard>
    </BrowserRouter>
  );
}

export default Router;
